import React, { useEffect, useLayoutEffect, useState } from "react";
import Loader from "../loader/Loader";
import Navigation from "../components/Navigation";
import { useDispatch, useSelector } from "react-redux";
import "./Survey.css";
import Nav from "react-bootstrap/Nav";
import Tab from "react-bootstrap/Tab";
import { useNavigate } from "react-router-dom";
import * as surveyActions from "../redux/survey/survey.actions";
import moment from "moment";
import {
  isUserParticipatedInSurveyAction,
  START_SURVEY_RESET,
} from "../redux/attemptSurvey/attemptSurvey.actions";

const Survey = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isSurveyDDViewing, setIsSurveyDDViewing] = useState(false);
  const [surveyFilter, setSurveyFilter] = useState("All");
  const [data, setData] = useState([]);
  const [activeData, setActiveData] = useState([]);
  const [completedData, setCompletedData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [innerLoading, setInnerLoading] = useState(true);
  const [isItalian, setIsItalian] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loading = useSelector((state) => state.surveyInfo.loading);
  const list = useSelector((state) => state.surveyInfo.list);
  const publicSurveyList = useSelector(
    (state) => state.surveyInfo.publicSurveyList
  );

  const profileData = useSelector((state) => state.authInfo.profileData);
  const isLoggedIn = !!profileData?.data?._id;

  useEffect(() => {
    const language = localStorage.getItem("language");
    setIsItalian(language === "Italian");
  }, []);

  useEffect(() => {
    dispatch(surveyActions.getOnlyPublicSurveyList());
    dispatch(surveyActions.getPublicSurveyList());
    dispatch({ type: START_SURVEY_RESET });
  }, [dispatch]);

  const { isUserParticipatedInSurvey } = useSelector(
    (state) => state.attemptSurveyInfo
  );
  // Helper to determine if contributed user has fully completed survey
  const isContributedAndAttemptedAllQuestions = (survey) => {
    if (
      survey.surveySteps &&
      Array.isArray(survey.surveySteps) &&
      survey.surveySteps.length > 0
    ) {
      const step = survey.surveySteps[0];
      // Check if surveyStep and totalSurvey are present and are numbers
      if (
        typeof step.surveyStep === "number" &&
        typeof step.totalSurvey === "number"
      ) {
        return step.surveyStep === step.totalSurvey;
      }
    }
    return false;
  };

  // Rebuild ContributedSurvey logic: only include surveys where surveyStep === totalSurvey (i.e., fully completed)
  const ContributedSurvey =
    isUserParticipatedInSurvey?.data?.filter((survey) => {
      // Only include if user has attempted all questions (i.e., surveyStep === totalSurvey)
      return isContributedAndAttemptedAllQuestions(survey);
    }) || [];

  useEffect(() => {
    if (list !== undefined && list !== null) {
      const active = (list || []).filter((elem) => elem.active);
      const completed =
        (publicSurveyList || []).filter((elem) => !elem.active).length > 0
          ? (publicSurveyList || []).filter((elem) => !elem.active)
          : (list || []).filter((elem) => !elem.active);

      // Combine all types of surveys into allData
      let combined = [...list];

      // Add any completed surveys from publicSurveyList that aren't in list
      if (publicSurveyList && publicSurveyList.length > 0) {
        for (const survey of publicSurveyList) {
          if (!list.some((item) => item._id === survey._id)) {
            combined.push(survey);
          }
        }
      }

      // Add any contributed surveys that aren't already in the combined list
      if (ContributedSurvey && ContributedSurvey.length > 0) {
        for (const survey of ContributedSurvey) {
          if (!combined.some((item) => item._id === survey._id)) {
            combined.push(survey);
          }
        }
      }

      setAllData(combined);
      setData(combined); // Initial data is all data
      setActiveData(active);
      setCompletedData(completed);
      setInnerLoading(false);
    }
  }, [list, publicSurveyList, ContributedSurvey]);

  useEffect(() => {
    if (surveyFilter === "All") {
      setData(allData || []);
    } else if (surveyFilter === "Active") {
      setData((list || []).filter((elem) => elem.active));
    } else if (surveyFilter === "Completed") {
      const completedFromPublic = (publicSurveyList || []).filter(
        (elem) => !elem.active
      );
      const completedFromList = (list || []).filter((elem) => !elem.active);
      setData(
        completedFromPublic.length > 0 ? completedFromPublic : completedFromList
      );
    } else if (surveyFilter === "Contributed" && isLoggedIn) {
      setData(ContributedSurvey || []);
    } else {
      setData(allData || []);
    }
  }, [surveyFilter, list, publicSurveyList, isLoggedIn, allData, ContributedSurvey]);

  useLayoutEffect(() => {
    const screenWidth = window.innerWidth;
    if (screenWidth <= 575) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(isUserParticipatedInSurveyAction());
    }
  }, [navigate, dispatch, isLoggedIn]);

  // Modified: If question(s) not completed, clicking card sends user to survey-details, not contributed-details
  const handleCardClick = (elem, isContributedCard) => {
    if (
      isContributedCard &&
      isUserParticipatedInSurvey?.data // Safety check
    ) {
      // Find the matching participated survey for the clicked card
      const found = isUserParticipatedInSurvey.data.find(
        (survey) => survey._id === elem._id
      );
      // Check: Has user completed ALL survey questions? (surveyStep === totalSurvey)
      let completedAll = false;
      if (
        found &&
        found.surveySteps &&
        Array.isArray(found.surveySteps) &&
        found.surveySteps.length > 0
      ) {
        const step = found.surveySteps[0];
        if (
          typeof step.surveyStep === "number" &&
          typeof step.totalSurvey === "number"
        ) {
          completedAll = step.surveyStep === step.totalSurvey;
        }
      }
      if (completedAll) {
        navigate(`/contributed-details/${elem._id}`);
      } else {
        navigate(`/survey-details/${elem._id}`);
      }
    } else {
      navigate(`/survey-details/${elem._id}`);
    }
  };

  const getSurveyStatusClass = (elem) => {
    if (ContributedSurvey.some((survey) => survey._id === elem._id)) {
      return "survey_card_contributed";
    } else if (elem.active) {
      return "survey_card_active";
    } else {
      return "survey_card_completed";
    }
  };

  const getSurveyStatusText = (elem) => {
    if (ContributedSurvey.some((survey) => survey._id === elem._id)) {
      return isItalian ? "Completato" : "Completed";
    } else if (elem.active) {
      return isItalian ? "Attivo" : "Active";
    } else {
      return isItalian ? "Terminato" : "Closed";
    }
  };

  // Used to tell if the data item should be shown as a "contributed" card
  const isContributedSurvey = (elem) => {
    return ContributedSurvey.some((survey) => survey._id === elem._id);
  };

  return (
    <>
      <Navigation />
      {loading || innerLoading ? (
        <Loader />
      ) : (
        <>
          <section className="survey">
            <div className="container">
              <div className="row">
                <div className="col-12 sticky_filter_section">
                  <span className="surveys_heading">
                    {isItalian ? "Sondaggi" : "Surveys"}
                  </span>
                  {isMobile && (
                    <div className="survey_dropdown_container">
                      <div
                        onClick={() => setIsSurveyDDViewing(!isSurveyDDViewing)}
                        className="survey_dropdown_btn"
                      >
                        {isItalian ? "Mostrando :" : "Showing :"}{" "}
                        <span>
                          {surveyFilter === "All"
                            ? isItalian
                              ? "Tutto"
                              : "All"
                            : surveyFilter === "Active"
                              ? isItalian
                                ? "Attivi"
                                : "Active"
                              : surveyFilter === "Completed"
                                ? isItalian
                                  ? "Terminati"
                                  : "Closed"
                                : surveyFilter === "Contributed"
                                  ? isItalian
                                    ? "Completato"
                                    : "Completed"
                                  : isItalian
                                    ? "Tutto"
                                    : "All"}
                          &nbsp;&nbsp;
                          <span>{data.length}</span>
                        </span>
                      </div>
                      {isSurveyDDViewing && (
                        <div className="survey_dropdown">
                          <ul>
                            <li>
                              <label htmlFor="flexCheckAll">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  id="flexCheckAll"
                                  name="surveyFilter"
                                  checked={surveyFilter === "All"}
                                  onChange={() => {
                                    setSurveyFilter("All");
                                    setIsSurveyDDViewing(false);
                                  }}
                                />
                                <span style={{ marginLeft: "5px" }}>
                                  {isItalian ? "Tutto" : "All"}
                                </span>
                              </label>
                            </li>
                            <li>
                              <label htmlFor="flexCheckActive">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  id="flexCheckActive"
                                  name="surveyFilter"
                                  checked={surveyFilter === "Active"}
                                  onChange={() => {
                                    setSurveyFilter("Active");
                                    setIsSurveyDDViewing(false);
                                  }}
                                />
                                <span style={{ marginLeft: "5px" }}>
                                  {isItalian ? "Attivi" : "Active"}
                                </span>
                              </label>
                            </li>
                            <li>
                              <label htmlFor="flexCheckCompleted">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  id="flexCheckCompleted"
                                  name="surveyFilter"
                                  checked={surveyFilter === "Completed"}
                                  onChange={() => {
                                    setSurveyFilter("Completed");
                                    setIsSurveyDDViewing(false);
                                  }}
                                />
                                <span style={{ marginLeft: "5px" }}>
                                  {isItalian ? "Terminati" : "Closed"}
                                </span>
                              </label>
                            </li>
                            {isLoggedIn && (
                              <li>
                                <label htmlFor="flexCheckContributed">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    id="flexCheckContributed"
                                    name="surveyFilter"
                                    checked={surveyFilter === "Contributed"}
                                    onChange={() => {
                                      setSurveyFilter("Contributed");
                                      setIsSurveyDDViewing(false);
                                    }}
                                  />
                                  <span style={{ marginLeft: "5px" }}>
                                    {isItalian ? "Completato" : "Completed"}
                                  </span>
                                </label>
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <Tab.Container defaultActiveKey="survey_tab_all">
                  <div className="col-12">
                    <div className="row mb-5">
                      {!isMobile && (
                        <div className="col-xl-3 col-lg-4 mt-4 surveys_left_column_wrapper">
                          <Nav
                            variant="pills"
                            className="flex-column surveys_left_column"
                          >
                            <Nav.Item>
                              <Nav.Link eventKey="survey_tab_all">
                                {isItalian ? "Tutto" : "All"}{" "}
                                {isMobile && (
                                  <>{isItalian ? "Sondaggio" : "Survey"}</>
                                )}
                                <span>{allData.length}</span>
                              </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                              <Nav.Link eventKey="survey_tab_active">
                                {isItalian ? "Attivi" : "Active"}{" "}
                                {isMobile && (
                                  <>{isItalian ? "Sondaggio" : "Survey"}</>
                                )}
                                <span>{activeData.length}</span>
                              </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                              <Nav.Link eventKey="survey_tab_completed">
                                {isItalian ? "Terminati" : "Closed"}{" "}
                                {isMobile && (
                                  <>{isItalian ? "Sondaggio" : "Survey"}</>
                                )}
                                <span>{completedData.length}</span>
                              </Nav.Link>
                            </Nav.Item>
                            {isLoggedIn && (
                              <Nav.Item>
                                <Nav.Link eventKey="survey_tab_contributed">
                                  {isItalian ? "Completati" : "Completed"}{" "}
                                  {isMobile && (
                                    <>{isItalian ? "Sondaggio" : "Survey"}</>
                                  )}
                                  <span>{ContributedSurvey.length}</span>
                                </Nav.Link>
                              </Nav.Item>
                            )}
                          </Nav>
                        </div>
                      )}
                      <div className="col-xl-9 col-lg-8 mt-4">
                        <Tab.Content className="surveys_right_column">
                          <Tab.Pane eventKey="survey_tab_all">
                            <div className="row g-3">
                              {data && data.length > 0 ? (
                                data.map((elem) => (
                                  <div
                                    className="col-xl-4 col-lg-6 col-md-6 col-sm-12"
                                    key={elem._id}
                                  >
                                    {console.log("ema : ", elem)}
                                    <div
                                      onClick={() =>
                                        handleCardClick(
                                          elem,
                                          isContributedSurvey(elem)
                                        )
                                      }
                                      className="survey_card"
                                    >
                                      <img
                                        src={
                                          elem?.banner
                                            ? `${process.env.REACT_APP_AWS_BUCKET_BASE_URL}${elem.banner}`
                                            : `${process.env.PUBLIC_URL}/images/votiamo_mini.png`
                                        }
                                        className="w-100 h-100 object-fit-cover"
                                        alt="Survey banner"
                                      />
                                      <span
                                        className={`survey_card_status ${getSurveyStatusClass(
                                          elem
                                        )}`}
                                      >
                                        {getSurveyStatusText(elem)}
                                      </span>
                                      <div className="survey_card_content">
                                        <h5 className="survey_card_title">
                                          {elem?.title}
                                        </h5>
                                        <div className="survey_card_extra">
                                          <span>
                                            <img
                                              src={`${process.env.PUBLIC_URL}/images/survey/calendarIcon.png`}
                                              alt="Calendar"
                                            />
                                            {moment(new Date(elem?.surveySettings?.endDate || elem?.setting[0]?.endDate)).format(
                                              "DD MMM, YY"
                                            )}
                                          </span>
                                          {/* {isContributedSurvey(elem) ? ( */}
                                            <span>
                                              <img
                                                src={`${process.env.PUBLIC_URL}/images/survey/peopleIcon.png`}
                                                alt="People"
                                              />
                                              {elem?.users?.length || elem?.attendedUsers}
                                            </span>
                                          {/* ) : (
                                            <span>
                                              <img
                                                src={`${process.env.PUBLIC_URL}/images/survey/peopleIcon.png`}
                                                alt="People"
                                              />
                                              0
                                            </span>
                                          )} */}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <h2 className="text-muted text-center mt-3">
                                  {isItalian
                                    ? "Nessun dato trovato"
                                    : "No Data Found"}
                                </h2>
                              )}
                            </div>
                          </Tab.Pane>
                          <Tab.Pane eventKey="survey_tab_active">
                            <div className="row g-3">
                              {activeData && activeData.length > 0 ? (
                                activeData.map((elem) => (
                                  <div
                                    className="col-xl-4 col-lg-6 col-md-6 col-sm-12"
                                    key={elem._id}
                                  >
                                    <div
                                      className="survey_card"
                                      onClick={() =>
                                        handleCardClick(elem, false)
                                      }
                                    >
                                      <img
                                        src={
                                          elem?.banner
                                            ? `${process.env.REACT_APP_AWS_BUCKET_BASE_URL}${elem.banner}`
                                            : `${process.env.PUBLIC_URL}/images/votiamo_mini.png`
                                        }
                                        className="w-100 h-100 object-fit-cover"
                                        alt="Survey banner"
                                      />
                                      <span className="survey_card_status survey_card_active">
                                        {isItalian ? "Attivo" : "Active"}
                                      </span>
                                      <div className="survey_card_content">
                                        <h5 className="survey_card_title">
                                          {elem?.title}
                                        </h5>
                                        <div className="survey_card_extra">
                                          <span>
                                            <img
                                              src={`${process.env.PUBLIC_URL}/images/survey/calendarIcon.png`}
                                              alt="Calendar"
                                            />
                                            {moment(new Date(elem?.surveySettings?.endDate || elem?.setting[0]?.endDate)).format(
                                              "DD MMM, YY"
                                            )}
                                          </span>
                                          {/* {isContributedSurvey(elem) ? ( */}
                                            <span>
                                              <img
                                                src={`${process.env.PUBLIC_URL}/images/survey/peopleIcon.png`}
                                                alt="People"
                                              />
                                              {elem?.users?.length || elem?.attendedUsers}
                                            </span>
                                          {/* ) : (
                                            <span>
                                              <img
                                                src={`${process.env.PUBLIC_URL}/images/survey/peopleIcon.png`}
                                                alt="People"
                                              />
                                              0
                                            </span>
                                          )} */}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <h2 className="text-muted text-center mt-3">
                                  {isItalian
                                    ? "Nessun dato trovato"
                                    : "No Data Found"}
                                </h2>
                              )}
                            </div>
                          </Tab.Pane>
                          <Tab.Pane eventKey="survey_tab_completed">
                            <div className="row g-3">
                              {completedData && completedData.length > 0 ? (
                                completedData.map((elem) => (
                                  <div
                                    className="col-xl-4 col-lg-6 col-md-6 col-sm-12"
                                    key={elem._id}
                                  >
                                    <div
                                      className="survey_card"
                                      onClick={() =>
                                        handleCardClick(elem, false)
                                      }
                                    >
                                      <img
                                        src={
                                          elem?.banner
                                            ? `${process.env.REACT_APP_AWS_BUCKET_BASE_URL}${elem.banner}`
                                            : `${process.env.PUBLIC_URL}/images/votiamo_mini.png`
                                        }
                                        className="w-100 h-100 object-fit-cover"
                                        alt="Survey banner"
                                      />
                                      <span className="survey_card_status survey_card_completed">
                                        {isItalian ? "Terminato" : "Closed"}
                                      </span>
                                      <div className="survey_card_content">
                                        <h5 className="survey_card_title">
                                          {elem?.title}
                                        </h5>
                                        <div className="survey_card_extra">
                                          <span>
                                            <img
                                              src={`${process.env.PUBLIC_URL}/images/survey/calendarIcon.png`}
                                              alt="Calendar"
                                            />
                                            {moment(new Date(elem?.surveySettings?.endDate || elem?.setting[0]?.endDate)).format(
                                              "DD MMM, YY"
                                            )}
                                          </span>
                                          {/* {isContributedSurvey(elem) ? ( */}
                                            <span>
                                              <img
                                                src={`${process.env.PUBLIC_URL}/images/survey/peopleIcon.png`}
                                                alt="People"
                                              />
                                              {elem?.users?.length || elem?.attendedUsers}
                                            </span>
                                          {/* ) : (
                                            <span>
                                              <img
                                                src={`${process.env.PUBLIC_URL}/images/survey/peopleIcon.png`}
                                                alt="People"
                                              />
                                              0
                                            </span>
                                          )} */}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <h2 className="text-muted text-center mt-3">
                                  {isItalian
                                    ? "Nessun dato trovato"
                                    : "No Data Found"}
                                </h2>
                              )}
                            </div>
                          </Tab.Pane>
                          {isLoggedIn && (
                            <Tab.Pane eventKey="survey_tab_contributed">
                              <div className="row g-3">
                                {ContributedSurvey &&
                                  ContributedSurvey.length > 0 ? (
                                  ContributedSurvey.map((elem) => (
                                    <div
                                      className="col-xl-4 col-lg-6 col-md-6 col-sm-12"
                                      key={elem._id}
                                    >
                                      <div
                                        className="survey_card"
                                        onClick={() =>
                                          handleCardClick(elem, true)
                                        }
                                      >
                                        <img
                                          src={
                                            elem?.banner
                                              ? `${process.env.REACT_APP_AWS_BUCKET_BASE_URL}${elem.banner}`
                                              : `${process.env.PUBLIC_URL}/images/votiamo_mini.png`
                                          }
                                          className="w-100 h-100 object-fit-cover"
                                          alt="Survey banner"
                                        />
                                        <span className="survey_card_status survey_card_contributed">
                                          {isItalian
                                            ? "Completato"
                                            : "Completed"}
                                        </span>
                                        <div className="survey_card_content">
                                          <h5 className="survey_card_title">
                                            {elem?.title}
                                          </h5>
                                          <div className="survey_card_extra">
                                            <span>
                                              <img
                                                src={`${process.env.PUBLIC_URL}/images/survey/calendarIcon.png`}
                                                alt="Calendar"
                                              />
                                              {moment(new Date(elem?.surveySettings?.endDate || elem?.setting[0]?.endDate)).format(
                                                "DD MMM, YY"
                                              )}
                                            </span>
                                            {/* {isContributedSurvey(elem) ? ( */}
                                              <span>
                                                <img
                                                  src={`${process.env.PUBLIC_URL}/images/survey/peopleIcon.png`}
                                                  alt="People"
                                                />
                                                {elem?.users?.length || elem?.attendedUsers}
                                              </span>
                                            {/* ) : (
                                              <span>
                                                <img
                                                  src={`${process.env.PUBLIC_URL}/images/survey/peopleIcon.png`}
                                                  alt="People"
                                                />
                                                0
                                              </span>
                                            )} */}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <h2 className="text-muted text-center mt-3">
                                    {isItalian
                                      ? "Nessun dato trovato"
                                      : "No Data Found"}
                                  </h2>
                                )}
                              </div>
                            </Tab.Pane>
                          )}
                        </Tab.Content>
                      </div>
                    </div>
                  </div>
                </Tab.Container>
              </div>
            </div>
          </section >
        </>
      )}
    </>
  );
};

export default Survey;
